import { useCallback, useState } from "react";
import { toast } from "~/components/ui/toaster";
import { type AdventureMap, createCuratorProfile, isCuratorProfileRequiredError, shareMap } from "../lib/api";
import { routes } from "../lib/routes";
import { useShare } from "../hooks/useShare";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

interface ShareDialogProps {
  map: AdventureMap;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when a share link is created for the first time */
  onShareLinkCreated?: (shareId: string) => void;
}

export default function ShareDialog({ map, open, onOpenChange, onShareLinkCreated }: ShareDialogProps) {
  const share = useShare();
  const hasLandingPage = !!map.landingPage?.publishedAt && !!map.landingPage?.slug;

  // Landing page mode: share landing page URL
  if (hasLandingPage) {
    const landingUrl = routes.to.landingPages.bySlug(map.landingPage!.slug!);

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>Share &ldquo;{map.name}&rdquo;</DialogTitle>
            <DialogDescription>Share your landing page with others.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 overflow-hidden">
            <label className="text-sm font-medium">Landing Page URL</label>
            <code className="block text-sm bg-muted px-3 py-2 rounded-lg font-mono truncate">{landingUrl}</code>
          </div>

          <DialogFooter className="mt-4">
            <Button onClick={() => share({ url: landingUrl, title: map.name })}>Copy Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Map share mode
  return <MapShareDialog map={map} open={open} onOpenChange={onOpenChange} onShareLinkCreated={onShareLinkCreated} />;
}

type ShareStep = "share" | "curator-profile";

function MapShareDialog({ map, open, onOpenChange, onShareLinkCreated }: ShareDialogProps) {
  const share = useShare();
  const [step, setStep] = useState<ShareStep>("share");
  const [isSharing, setIsSharing] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [savingCurator, setSavingCurator] = useState(false);

  const handleShare = useCallback(async () => {
    setIsSharing(true);
    try {
      const updated = await shareMap(map.id);
      onShareLinkCreated?.(updated.shareId!);
      toast.success("Share link created");
    } catch (err) {
      if (isCuratorProfileRequiredError(err)) {
        setStep("curator-profile");
      } else {
        toast.error("Failed to create share link");
      }
    } finally {
      setIsSharing(false);
    }
  }, [map.id, onShareLinkCreated]);

  const handleCuratorCreate = useCallback(async () => {
    if (!displayName.trim()) return;

    setSavingCurator(true);
    try {
      await createCuratorProfile({ displayName: displayName.trim(), trigger: "share_map" });
      // Profile created, now create the share link
      const updated = await shareMap(map.id);
      onShareLinkCreated?.(updated.shareId!);
      setStep("share");
      toast.success("Share link created");
    } catch {
      toast.error("Failed to create profile");
    } finally {
      setSavingCurator(false);
    }
  }, [displayName, map.id, onShareLinkCreated]);

  // Already shared: show URL with copy button
  if (map.shareId) {
    const shareUrl = routes.to.maps.publicViewUrl(map.name, map.shareId);

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>Share &ldquo;{map.name}&rdquo;</DialogTitle>
            <DialogDescription>Share this link with others to let them view your map.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 overflow-hidden">
            <label className="text-sm font-medium">Share URL</label>
            <code className="block text-sm bg-muted px-3 py-2 rounded-lg font-mono truncate">
              {shareUrl.replace(/^https?:\/\//, "")}
            </code>
          </div>

          <DialogFooter className="mt-4">
            <Button onClick={() => share({ url: shareUrl, title: map.name })}>Copy Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Not shared yet
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-md">
        {step === "curator-profile" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCuratorCreate();
            }}
          >
            <DialogHeader>
              <DialogTitle>Set up your curator profile</DialogTitle>
              <DialogDescription>
                Before sharing your map publicly, let us know who you are. Your name will be shown as the curator.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2 py-4">
              <Label htmlFor="display-name">Display name</Label>
              <Input
                id="display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                maxLength={100}
                autoFocus
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep("share")}>
                Back
              </Button>
              <Button type="submit" disabled={!displayName.trim() || savingCurator}>
                {savingCurator ? "Creating..." : "Create & Share"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Share &ldquo;{map.name}&rdquo;</DialogTitle>
              <DialogDescription>Create a shareable link for your map.</DialogDescription>
            </DialogHeader>

            <DialogFooter className="mt-4">
              <Button onClick={handleShare} disabled={isSharing}>
                {isSharing ? "Creating..." : "Create Share Link"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
